// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import styles from './ActivityRail.module.css';

/** The Activities-zone tabs (MVP concept 03/05). */
export type ActivityTab = 'variable' | 'play' | 'signal' | 'agents';

export interface ActivityRailTab {
  id: ActivityTab;
  label: string;
  /**
   * Disabled tabs render greyed and are not selectable -- used for surfaces not
   * built yet (Signal in WS3, Agents in WS4/WS5). They stay visible so the rail's
   * shape is constant across tiers/increments ("the layout never moves").
   */
  enabled: boolean;
}

/**
 * The MVP activity tabs, in order. Variable (the inherited variable editor) and
 * Play land in WS2; Signal (WS3) and Agents (WS4/WS5) are placeholders shown
 * disabled until their surfaces are built.
 */
export const DEFAULT_ACTIVITY_TABS: readonly ActivityRailTab[] = [
  { id: 'variable', label: 'Variable', enabled: true },
  // The 'play' tab id is kept; label renamed to "Levers" (Williams 2026-08-07) --
  // the tab holds the parameter levers.
  { id: 'play', label: 'Levers', enabled: true },
  { id: 'signal', label: 'Signal', enabled: false },
  { id: 'agents', label: 'Agents', enabled: false },
];

interface ActivityRailProps {
  /** Which tab's content is shown. */
  activeTab: ActivityTab;
  /** Called with the new tab id when the user selects an enabled, inactive tab. */
  onTabChange: (tab: ActivityTab) => void;
  /** Tab definitions (order + enabled). Defaults to DEFAULT_ACTIVITY_TABS. */
  tabs?: readonly ActivityRailTab[];
  /** The active tab's panel, supplied by the Editor. */
  children?: React.ReactNode;
}

/**
 * The shared tabbed right rail -- the Activities zone that Play, the B* Signal
 * panel, and the agent panels plug into (MVP WS2, concept 03/05). The rail renders
 * the tab bar and shows `children` (the active tab's panel, supplied by the
 * Editor), so it owns no content of its own. Building it once in WS2 gives WS3/WS4
 * /WS5 their home without a second layout.
 *
 * Presentation-only, like RenderModeToggle: `activeTab` is a prop and selections
 * flow back through `onTabChange`, keeping the Editor the single source of truth.
 */
export class ActivityRail extends React.PureComponent<ActivityRailProps> {
  render(): React.ReactNode {
    const { activeTab, onTabChange, tabs = DEFAULT_ACTIVITY_TABS, children } = this.props;
    return (
      <div className={styles.rail}>
        <div className={styles.tabBar} role="tablist" aria-label="Activities">
          {tabs.map((tab) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                className={selected ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                aria-selected={selected}
                aria-disabled={!tab.enabled}
                disabled={!tab.enabled}
                title={tab.enabled ? undefined : `${tab.label} — coming soon`}
                onClick={() => {
                  if (tab.enabled && !selected) {
                    onTabChange(tab.id);
                  }
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className={styles.content} role="tabpanel">
          {children}
        </div>
      </div>
    );
  }
}
